import React, { useMemo, useState } from 'react';
import {
  Stack,
  Box,
  Container,
  Grid,
  Typography,
  Tooltip,
  IconButton,
  CardHeader,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  Card,
  ProfileCardContent,
  LabelAndValueElement,
  useCurrentTime,
  ContextMenuComplete,
  ZAvatar,
  OutlinedIcons,
  OtherChips,
  TravelerTagChips,
  useApiClient,
  useFetchImmutable,
} from '@marageti/z4-lib';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import {
  CoTravelerSummary, Traveler,
} from '@marageti/z4-sdk/lib/people';
import { Partner } from '@marageti/z4-sdk/lib/travel';
import styles from './styles';
import {
  getHistory, getPhoneNumber, getPersonTime, getCountryOfResidence,
} from '../../../utils/helpers';
import { EditTravelerForm } from '../../../components/modals/edit-traveler-form';
import { EditTravelerEmailForm } from '../../../components/modals/edit-traveler-email-form';
import TravelHistoryModal, { SelectedTab } from '../../../components/travel-history-modal';
import RemindersSummaryContainer from '../../../components/reminders-summary';
import { useJustCallDialer } from '../../../hooks/use-justcall-dialer';
import { FEATURES, FeatureFlag } from '../../../store/contexts/feature-flags';
import TravelerPersonalDetailsContainer from '../../../components/traveler-details';
import TravelHistoryCard from './travel-history-card';
import TravelerProfileCountriesContainer from '../../../components/traveler-profile-countries';
import TravelerProfileBucketListContainer from '../../../components/traveler-profile-bucket-list';
import TravelWithSection from '../../../components/travel-with-section';
import TravelerProfileNotesSection from '../../../components/traveler-profile-notes';
import TravelPreferencesCard from '../../../components/traveler-profile-preferences';
import TravelerProfileRichTextSections from '../../../components/traveler-profile-rich-text-sections';
import TravelerProfileSignificantDates from '../../../components/traveler-profile-significant-dates';
import { TravelerProfileSuggestionsProvider } from '../../../store/contexts/traveler-profile-suggestions';
import {
  SuggestionsHost,
  AiEntryAlert,
  SuggestionValidationAlert,
  NoSuggestionsAlert,
} from '../../../components/traveler-profile-suggestions';

const { getTravelerTagChip } = TravelerTagChips;
const { ChipRepeat } = OtherChips;

type ProfileProps = {
  travelerId: string | undefined;
  canEdit: boolean;
  onProfileEdit?: () => void;
  referringPartner: Partner | undefined;
  traveler: Traveler | undefined;
  totalTripRequests: number | undefined;
  totalSoldTrips: number | undefined;
  totalReviews: number | undefined;
  coTravelers: CoTravelerSummary[] | undefined;
  onAvatarEdit?: (avatar: string) => void;
};

const getCountryFullNameString = (country: string | undefined): string | undefined => {
  const apiClient = useApiClient();
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  if (country && country !== '-' && countryCodes && countryCodes[country]) {
    return countryCodes[country];
  }
  return country;
};

const TimeZoneWithCurrentTime = ({ traveler }: { traveler: Traveler }) => {
  const [time] = useCurrentTime(traveler?.timeZone?.name);

  const timeZone = useMemo(() => getPersonTime(traveler, time), [traveler, time]);

  return (
    <LabelAndValueElement
      title="Timezone"
      value={timeZone}
      key="timeZone"
    />
  );
};

// Clickable stat components
type StatProps = {
  onClick: () => void;
  count: number | undefined;
  label: string;
};

const ClickableStat = ({ onClick, count, label }: StatProps) => (
  <Typography
    variant="captionBold"
    color="primary"
    component="span"
    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
    onClick={onClick}
  >
    {count !== undefined ? `${label} (${count})` : `${label} (-)`}
  </Typography>
);

type ReviewsStatProps = {
  count: number | undefined;
  travelerId: string | undefined;
};

const ReviewsStat = ({ count, travelerId }: ReviewsStatProps) => (
  <Typography
    to={travelerId ? `/travelers/${travelerId}/reviews` : '#'}
    component={Link}
    variant="captionBold"
    color="primary"
    sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
  >
    {count !== undefined ? `Reviews (${count})` : 'Reviews (-)'}
  </Typography>
);

const ProfileView = ({
  travelerId,
  canEdit,
  onProfileEdit,
  referringPartner,
  traveler,
  totalTripRequests,
  totalSoldTrips,
  totalReviews,
  coTravelers,
  onAvatarEdit,
}: ProfileProps) => {
  const [isProfileEditOpen, setIsProfileEditOpen] = useState(false);
  const [isEmailEditOpen, setIsEmailEditOpen] = useState(false);
  const [isTravelHistoryModalOpen, setIsTravelHistoryModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<SelectedTab>('trip-requests');
  const {
    launchDialerWithNumber,
    isDialerLockBlocked,
    isDialerLockHeldRemotely,
  } = useJustCallDialer();

  const phoneDialerRemoteDisabled = isDialerLockHeldRemotely;
  let phoneDialerTooltipTitle = 'Start a Call/SMS';
  if (isDialerLockBlocked) {
    phoneDialerTooltipTitle = 'Phone active in another tab or window';
  } else if (isDialerLockHeldRemotely) {
    phoneDialerTooltipTitle = 'Dialer is open in another tab';
  }

  const onClickProfileEdit = () => {
    setIsProfileEditOpen(true);
  };

  const handleCloseProfileEdit = () => {
    setIsProfileEditOpen(false);
  };

  const onClickEmailEdit = () => {
    setIsEmailEditOpen(true);
  };

  const handleCloseEmailEdit = () => {
    setIsEmailEditOpen(false);
  };

  const onClickTravelHistoryModal = (initialTab: SelectedTab) => {
    setSelectedTab(initialTab);
    setIsTravelHistoryModalOpen(true);
  };

  const handleCloseTravelHistoryModal = () => {
    setIsTravelHistoryModalOpen(false);
  };

  const travelerHasCallablePhone = Boolean(traveler?.phone?.number);

  const handleStartACall = () => {
    if (!traveler?.phone?.number) return;
    const prefix = traveler.phone.prefix?.trim() || '';
    const number = traveler.phone.number.trim();
    launchDialerWithNumber(`${prefix}${number}`);
  };

  return (
    // Keyed on `travelerId` so navigating between travelers fully tears down
    // the suggestions hook (resetting skipped/entry-alert state, in-flight
    // requests, accepted-but-uncommitted suggestions, etc.).
    <TravelerProfileSuggestionsProvider
      key={travelerId ?? 'no-traveler'}
      travelerId={travelerId}
      canEdit={canEdit}
    >
      <AiEntryAlert />
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <NoSuggestionsAlert />
          <SuggestionValidationAlert />
          <Grid container spacing={3}>
            {/* Left Column - Profile Card & Travel History */}
            <Grid item xs={12} md={5} display="flex" flexDirection="column" gap={3}>
              <Card sx={styles.profileCard}>
                <ProfileCardContent>
                  <Stack spacing={3} width="100%">
                    <CardHeader
                      title="Account Info"
                      titleTypographyProps={{ variant: 'h3' }}
                      action={canEdit ? (
                        <ContextMenuComplete
                          id="edit-profile-context-menu"
                          items={[
                            { action: onClickProfileEdit, label: 'Edit Profile' },
                            { action: onClickEmailEdit, label: 'Edit Email Address' },
                          ]}
                        />
                      ) : undefined}
                      sx={{ p: 0 }}
                    />
                    <Stack direction="row" alignItems="center" spacing={3}>
                      <ZAvatar
                        name={traveler?.fullName}
                        size={128}
                        loading={!traveler}
                        onEdit={canEdit ? onAvatarEdit : undefined}
                        src={traveler?.avatar}
                      />
                      <Stack direction="column" spacing={0.5}>
                        <Typography variant="h3">
                          {traveler?.fullName}
                        </Typography>
                        <Stack direction="row" spacing={0.5}>
                          {traveler?.tags?.map((tag) => getTravelerTagChip(tag, 'small'))}
                          {!!traveler?.totalZicassoTrips && <ChipRepeat />}
                        </Stack>
                      </Stack>
                    </Stack>
                    <LabelAndValueElement
                      title="Country of Residence"
                      value={getCountryFullNameString(getCountryOfResidence(traveler))}
                      key="residence"
                    />
                    {travelerHasCallablePhone ? (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <LabelAndValueElement
                          title="Phone"
                          value={getPhoneNumber(traveler)}
                          key="phone"
                        />
                        <FeatureFlag feature={FEATURES.JUST_CALL_DIALER}>
                          <Tooltip placement="top" title={phoneDialerTooltipTitle}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={handleStartACall}
                                disabled={phoneDialerRemoteDisabled}
                                aria-label={
                                  isDialerLockHeldRemotely
                                    ? 'Phone — dialer active in another tab or window'
                                    : 'start a call'
                                }
                              >
                                <OutlinedIcons.OutlinedPhoneIcon color="primary" fontSize="inherit" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </FeatureFlag>
                      </Stack>
                    ) : (
                      <LabelAndValueElement
                        title="Phone"
                        value={getPhoneNumber(traveler)}
                        key="phone"
                      />
                    )}
                    <TimeZoneWithCurrentTime
                      traveler={traveler || {} as Traveler}
                    />
                    <LabelAndValueElement
                      title="Email"
                      value={traveler?.email}
                      key="email"
                    />
                    <LabelAndValueElement
                      title="History"
                      value={traveler ? `Member since ${getHistory(traveler) || ''}` : undefined}
                      key="history"
                    />
                    <LabelAndValueElement
                      title="PRL Status"
                      key="PRLStatus"
                      value={referringPartner?.name || (traveler ? '-' : undefined)}
                    />
                    {/* Clickable Stats */}
                    <Stack direction="row" spacing={3}>
                      <ClickableStat
                        onClick={() => onClickTravelHistoryModal('trip-requests')}
                        count={totalTripRequests}
                        label="Trip Requests"
                      />
                      <ClickableStat
                        onClick={() => onClickTravelHistoryModal('sold-trips')}
                        count={totalSoldTrips}
                        label="Sold Trips"
                      />
                      <ReviewsStat
                        count={totalReviews}
                        travelerId={traveler?.id}
                      />
                    </Stack>
                  </Stack>
                </ProfileCardContent>
              </Card>

              <TravelerPersonalDetailsContainer
                travelerId={travelerId}
                canEdit={canEdit}
              />

              <TravelHistoryCard>
                <TravelWithSection coTravelers={coTravelers} />
                <TravelerProfileSignificantDates travelerId={travelerId} canEdit={canEdit} />
                <TravelerProfileRichTextSections travelerId={travelerId} canEdit={canEdit} />
                <TravelerProfileBucketListContainer travelerId={travelerId} canEdit={canEdit} />
                <TravelerProfileCountriesContainer travelerId={travelerId} canEdit={canEdit} />
              </TravelHistoryCard>
            </Grid>

            {/* Right Column - Travel Preferences, Notes & Reminders */}
            <Grid item xs={12} md={7} display="flex" gap={3} flexDirection="column">
              {travelerId && (
                <TravelPreferencesCard travelerId={travelerId} canEdit={canEdit} />
              )}

              {travelerId && (
                <TravelerProfileNotesSection travelerId={travelerId} />
              )}

              {/* Reminders Summary */}
              {traveler?.id && (
                <RemindersSummaryContainer
                  tripRequestId=""
                  travelerId={traveler.id}
                  showYear
                  showFilters
                  emptyMessage="No reminders have been created for this traveler."
                />
              )}
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Modals */}
      {traveler && (
        <>
          <EditTravelerForm
            open={isProfileEditOpen}
            onClose={handleCloseProfileEdit}
            traveler={traveler}
            referringPartner={referringPartner}
            onSubmitCallback={() => {
              if (onProfileEdit) onProfileEdit();
            }}
          />
          <EditTravelerEmailForm
            open={isEmailEditOpen}
            onClose={handleCloseEmailEdit}
            traveler={traveler}
          />
          <TravelHistoryModal
            open={isTravelHistoryModalOpen}
            onClose={handleCloseTravelHistoryModal}
            traveler={traveler}
            selectedTab={selectedTab}
            onTabChange={setSelectedTab}
          />
        </>
      )}

      <SuggestionsHost />
    </TravelerProfileSuggestionsProvider>
  );
};

export default ProfileView;
